"use client";

import { c as _c } from "react/compiler-runtime";
import { Button, Link, Tooltip } from "@dc/ui/components";
import { cn } from "@dc/ui/lib/utils";
import { useTranslations } from "next-intl";
import { SettingsLinear } from "solar-icon-set";
import { useSettingsEntry } from "@/hooks/useSettingsEntry";
import { useOrgTeamPath } from "@/lib/orgTeamPath";
import type { SidebarSettingsButtonProps } from "./SidebarSettingsButton.types";
interface SettingsNavLinkProps {
  href: string;
  label: string;
  testId: string;
  isActive: boolean;
  isCollapsed: boolean;
  isMobileVariant: boolean;
  onPress?: () => void;
}

// The sidebar's mobile and desktop settings buttons have always used different
// active styling; keep them distinct so neither placement changes visually.
const SettingsNavLink = t0 => {
  const $ = _c(26);
  const {
    href,
    label,
    testId,
    isActive,
    isCollapsed,
    isMobileVariant,
    onPress
  } = t0;
  const t1 = !isCollapsed;
  const t2 = isMobileVariant && isActive;
  const t3 = !isMobileVariant && isActive;
  let t4;
  if ($[0] !== isCollapsed || $[1] !== t2 || $[2] !== t3) {
    t4 = cn("justify-start truncate bg-transparent text-sm text-zinc-300 transition-all hover:bg-navy-700 hover:text-zinc-300!", {
      "justify-center": isCollapsed,
      "bg-navy-900! font-medium": t2,
      "bg-navy-900": t3
    });
    $[0] = isCollapsed;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== isActive || $[5] !== isCollapsed || $[6] !== isMobileVariant) {
    t5 = isCollapsed ? null : isMobileVariant ? <SettingsLinear className={cn("size-6", isActive ? "text-default-900" : "")} /> : <SettingsLinear className="size-6" />;
    $[4] = isActive;
    $[5] = isCollapsed;
    $[6] = isMobileVariant;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== isActive || $[9] !== isCollapsed || $[10] !== isMobileVariant || $[11] !== label) {
    t6 = isCollapsed ? isMobileVariant ? <SettingsLinear className={cn("size-6", isActive ? "text-default-900" : "text-default-500")} /> : <SettingsLinear className="size-6 text-default-500" /> : label;
    $[8] = isActive;
    $[9] = isCollapsed;
    $[10] = isMobileVariant;
    $[11] = label;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== href || $[14] !== isCollapsed || $[15] !== label || $[16] !== onPress || $[17] !== t4 || $[18] !== t5 || $[19] !== t6 || $[20] !== testId) {
    t7 = <div className="w-full"><Button fullWidth={true} className={t4} isIconOnly={isCollapsed} as={Link} href={href} onPress={onPress} startContent={t5} data-testid={testId} aria-label={label}>{t6}</Button></div>;
    $[13] = href;
    $[14] = isCollapsed;
    $[15] = label;
    $[16] = onPress;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = testId;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== label || $[23] !== t1 || $[24] !== t7) {
    t8 = <Tooltip content={label} isDisabled={t1} placement="right" color="foreground">{t7}</Tooltip>;
    $[22] = label;
    $[23] = t1;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
};
export const SidebarSettingsButton = t0 => {
  const $ = _c(14);
  const {
    organizationId,
    isCollapsed,
    isOrgSettingsActive,
    isTeamSettingsActive,
    onPress,
    variant
  } = t0;
  const t = useTranslations("navigation.actions");
  const {
    build: buildPath
  } = useOrgTeamPath();
  const entry = useSettingsEntry(organizationId || undefined);
  if (!entry) {
    return null;
  }
  const isMobileVariant = variant === "mobile";
  let t1;
  if ($[0] !== buildPath || $[1] !== entry.href) {
    t1 = buildPath(entry.href);
    $[0] = buildPath;
    $[1] = entry.href;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== entry.kind || $[4] !== t) {
    t2 = entry.kind === "org" ? t("settings") : t("team_settings");
    $[3] = entry.kind;
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = entry.kind === "org" ? "sidebar-settings" : "sidebar-team-settings";
  const t4 = entry.kind === "org" ? isOrgSettingsActive : isTeamSettingsActive;
  let t5;
  if ($[6] !== isCollapsed || $[7] !== isMobileVariant || $[8] !== onPress || $[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <SettingsNavLink href={t1} label={t2} testId={t3} isActive={t4} isCollapsed={isCollapsed} isMobileVariant={isMobileVariant} onPress={onPress} />;
    $[6] = isCollapsed;
    $[7] = isMobileVariant;
    $[8] = onPress;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};